import React, { useContext, useEffect, useMemo, useState } from 'react'
import { FaRegBell } from 'react-icons/fa'
import api from '../../utils/api'
import { CaseDetailContext } from './CaseDetailContext'

/**
 * EmailReminder — right-sidebar widget matching Scale's "Trigger Reminder
 * Email" card exactly (per the screenshot).
 *
 *   Header: "Trigger Reminder Email" / "Send manual reminders to clients"
 *   Party dropdown: "Plaintiff: {name}" / "Defendant: {name}"
 *   Section: "Select Reminder Emails"
 *   2x2 checkbox grid:
 *     Onboarding Reminder        | Mediation Statement Reminder
 *     Payment Reminder           | Zoom Invitation
 *   Bottom: "Send Email" button with red bell icon
 *
 * Each reminder type is auto-disabled when no longer applicable (e.g.
 * Payment Reminder is disabled if the payment is already paid OR no
 * payment_url exists yet).
 */

function ReminderCheckbox({ label, checked, disabled, onChange }) {
    return (
        <label className={`flex items-start gap-2 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
            <input
                type="checkbox"
                disabled={disabled}
                checked={!!checked}
                onChange={(e) => !disabled && onChange(e.target.checked)}
                className="w-4 h-4 mt-0.5 text-teal-600 rounded border-gray-300 focus:ring-teal-500 disabled:opacity-50"
            />
            <span className="text-xs text-gray-800 leading-tight">{label}</span>
        </label>
    )
}

export default function EmailReminder({ onToast }) {
    const { caseData, is_cancled_case, is_odr_mediator, plaintiffPartyLabel, defendantPartyLabel } = useContext(CaseDetailContext)
    // Scale defaults to "Brief"; mediator-specific brief_text overrides.
    const briefLabel = caseData?.mediatorSettings?.brief_text || 'Brief'

    const allParties = useMemo(() => {
        const parties = []
        const pid = caseData?.plaintiff?.client_id || caseData?.plaintiff?.clientId
        const did = caseData?.defendant?.client_id || caseData?.defendant?.clientId
        if (pid) {
            parties.push({
                client_id: pid,
                name: caseData.plaintiff.name,
                role: 'plaintiff',
                alternate_emails: caseData.plaintiff.alternate_emails || [],
                onboarding: caseData.plaintiff.onboarding,
                payment: caseData.plaintiff.payment,
                label: `${plaintiffPartyLabel}: ${caseData.plaintiff.name || ''}`,
            })
            ;(Array.isArray(caseData.plaintiff.additionalParties) ? caseData.plaintiff.additionalParties : []).forEach((p) => parties.push({
                client_id: p.client_id || p.client?.client_id,
                name: p.name || p.client?.name,
                role: p.role || 'plaintiff',
                position: p.position,
                primary_party_id: pid,
                is_additional_party: true,
                alternate_emails: p.alternate_emails || [],
                onboarding: p.onboarding,
                payment: p.payment,
                label: `${plaintiffPartyLabel} ${p.position || 'Additional'}: ${p.client?.name || p.name || ''}`,
            }))
        }
        if (did) {
            parties.push({
                client_id: did,
                name: caseData.defendant.name,
                role: 'defendant',
                alternate_emails: caseData.defendant.alternate_emails || [],
                onboarding: caseData.defendant.onboarding,
                payment: caseData.defendant.payment,
                label: `${defendantPartyLabel}: ${caseData.defendant.name || ''}`,
            })
            ;(Array.isArray(caseData.defendant.additionalParties) ? caseData.defendant.additionalParties : []).forEach((p) => parties.push({
                client_id: p.client_id || p.client?.client_id,
                name: p.name || p.client?.name,
                role: p.role || 'defendant',
                position: p.position,
                primary_party_id: did,
                is_additional_party: true,
                alternate_emails: p.alternate_emails || [],
                onboarding: p.onboarding,
                payment: p.payment,
                label: `${defendantPartyLabel} ${p.position || 'Additional'}: ${p.client?.name || p.name || ''}`,
            }))
        }
        return parties
    }, [caseData, plaintiffPartyLabel, defendantPartyLabel])

    const [selectedClientId, setSelectedClientId] = useState(allParties[0]?.client_id || '')
    const [checked, setChecked] = useState({})
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        if (allParties.length && !allParties.some((p) => p.client_id === selectedClientId)) {
            setSelectedClientId(allParties[0].client_id)
            setChecked({})
        }
    }, [allParties, selectedClientId])

    const selected = allParties.find((p) => p.client_id === selectedClientId) || null

    const reminders = useMemo(() => {
        if (!selected) return { onboarding: { disabled: true }, brief: { disabled: true }, payment: { disabled: true }, zoom: { disabled: true } }
        const onboardingDone = !!selected.onboarding?.completed
        const briefDone = (selected.onboarding?.brief_info?.length || 0) > 0 || !!selected.onboarding?.is_brief_submit_manually
        const paymentStatus = (selected.payment?.status || '').toLowerCase()
        const paymentPaid = paymentStatus === 'paid' || paymentStatus === 'paid_manually'
        // Scale parity (CaseManagement/EmailReminder.jsx): Payment Reminder is
        // enabled ONLY when the participant has onboarding data AND a payable
        // invoice — amount > 0 AND a payment_url — and it isn't already paid.
        // CM previously used OR on amount/url and ignored onboarding, so the
        // checkbox stayed enabled in cases where Scale disables it.
        const paymentReady =
            !!selected.onboarding &&
            (selected.payment?.amount || 0) > 0 &&
            !!selected.payment?.payment_url
        return {
            onboarding: { disabled: onboardingDone },
            brief: { disabled: !onboardingDone || briefDone },
            payment: { disabled: !paymentReady || paymentPaid },
            zoom: { disabled: !caseData?.zoomLink },
        }
    }, [selected, caseData])

    const handleSend = async () => {
        if (!selected) return
        const types = Object.keys(checked).filter((k) => checked[k])
        if (types.length === 0) {
            onToast?.({ type: 'warning', message: 'Pick at least one reminder type' })
            return
        }
        try {
            setLoading(true)
            const partyType = selected.role.includes('defendant') ? 'defendant' : 'plaintiff'
            await api.post(`/cases/${caseData.id}/send-email`, {
                emailTypes: types,
                clientId: selected.client_id,
                partyType,
                clients: types.map((type) => ({
                    client_id: selected.client_id,
                    role: selected.role,
                    type,
                    event: `${type.charAt(0).toUpperCase() + type.slice(1)} Reminder Sent Manually`,
                    case_id: caseData?.id,
                    plaintiff_id: partyType === 'plaintiff' ? (selected.is_additional_party ? selected.primary_party_id : selected.client_id) : null,
                    defender_id: partyType === 'defendant' ? (selected.is_additional_party ? selected.primary_party_id : selected.client_id) : null,
                    additional_client_id: selected.is_additional_party ? selected.client_id : null,
                    mediator: caseData?.mediatorId || caseData?.mediator?.userId || null,
                    alternate_emails: selected.alternate_emails || [],
                })),
            })
            onToast?.({ type: 'success', message: 'Email sent successfully' })
            setChecked({})
        } catch (err) {
            console.error(err)
            onToast?.({ type: 'error', message: 'Failed to send reminder' })
        } finally {
            setLoading(false)
        }
    }

    const noActions = Object.values(checked).every((v) => !v)

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-200">
                <div className="text-base font-semibold text-gray-900">Trigger Reminder Email</div>
                <p className="text-xs text-gray-500 mt-0.5">Send manual reminders to clients</p>
                <select
                    value={selectedClientId || ''}
                    onChange={(e) => { setSelectedClientId(Number(e.target.value)); setChecked({}) }}
                    className="mt-3 w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                >
                    {allParties.length === 0 && <option value="">No parties</option>}
                    {allParties.map((p) => (
                        <option key={p.client_id} value={p.client_id}>{p.label}</option>
                    ))}
                </select>
            </div>

            <div className="px-5 py-4 space-y-3">
                <p className="text-xs font-semibold text-gray-700">Select Reminder Emails</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    <ReminderCheckbox
                        label="Onboarding Reminder"
                        checked={!!checked.onboarding}
                        disabled={reminders.onboarding.disabled || is_cancled_case}
                        onChange={(v) => setChecked((c) => ({ ...c, onboarding: v }))}
                    />
                    {!is_odr_mediator && (
                        <ReminderCheckbox
                            label={`${briefLabel} Reminder`}
                            checked={!!checked.brief}
                            disabled={reminders.brief.disabled || is_cancled_case}
                            onChange={(v) => setChecked((c) => ({ ...c, brief: v }))}
                        />
                    )}
                    <ReminderCheckbox
                        label="Payment Reminder"
                        checked={!!checked.payment}
                        disabled={reminders.payment.disabled || is_cancled_case}
                        onChange={(v) => setChecked((c) => ({ ...c, payment: v }))}
                    />
                    <ReminderCheckbox
                        label="Zoom Invitation"
                        checked={!!checked.zoom}
                        disabled={reminders.zoom.disabled || is_cancled_case}
                        onChange={(v) => setChecked((c) => ({ ...c, zoom: v }))}
                    />
                </div>
                <button
                    type="button"
                    disabled={loading || is_cancled_case || noActions}
                    onClick={handleSend}
                    title={is_cancled_case ? 'Case is canceled!' : ''}
                    className="w-full flex items-center justify-center gap-2 bg-[#173336] hover:bg-[#0d2226] text-white rounded-md py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    {loading ? 'Sending…' : 'Send Email'}
                    <FaRegBell size={14} className="text-red-400" />
                </button>
            </div>
        </div>
    )
}
